import { useTranslation } from 'react-i18next';

import {
  type Translate,
  useNotificationPolicy,
  useNotificationPreferences,
  useSetNotificationPolicy,
  useSetNotificationPreferences,
} from '@/entities/collab';
import type { Workspace } from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import { ErrorState, LoadingState } from '@/shared/ui';

import styles from './Admin.module.css';

/**
 * Which administrative events produce notifications in this workspace (the policy, for those who
 * may configure it) and which of those the viewer wants to receive (personal preferences).
 * Mandatory notifications (access, mentions, assignments, invitations) are listed, never off.
 */
export function NotificationsTab({ workspace }: { workspace: Workspace }) {
  const { t } = useTranslation();
  const tr = t as unknown as Translate;
  const policy = useNotificationPolicy(workspace.id);
  const preferences = useNotificationPreferences(workspace.id);
  const setPolicy = useSetNotificationPolicy(workspace.id);
  const setPreferences = useSetNotificationPreferences(workspace.id);

  if (policy.isPending || preferences.isPending) return <LoadingState />;
  if (policy.isError) return <ErrorState error={policy.error} />;
  if (preferences.isError) return <ErrorState error={preferences.error} />;
  const personal = new Map(preferences.data.categories.map((c) => [c.id, c]));
  return (
    <div className={styles.panel}>
      <p className={styles.note}>{t('workspaceAdmin.notificationsHint')}</p>
      <FormError error={setPolicy.error ?? setPreferences.error} />
      <ul className={styles.list} aria-label={t('workspaceAdmin.tabs.notifications')}>
        {policy.data.categories.map((category) => {
          const mine = personal.get(category.id);
          return (
            <li key={category.id} className={styles.notificationRow} data-testid="policy-row">
              <span className={styles.grow}>
                <span>{tr(`notificationPolicy.categories.${category.id}.title`)}</span>
                <span className={styles.meta}>
                  {tr(`notificationPolicy.categories.${category.id}.hint`)}
                </span>
              </span>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={category.enabled}
                  disabled={!policy.data.can_manage || setPolicy.isPending}
                  aria-label={t('notificationPolicy.workspaceToggle', {
                    category: tr(`notificationPolicy.categories.${category.id}.title`),
                  })}
                  onChange={(event) => {
                    setPolicy.mutate({ [category.id]: event.target.checked });
                  }}
                />
                <span>{t('notificationPolicy.forWorkspace')}</span>
              </label>
              <label className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={mine?.enabled ?? false}
                  disabled={!(mine?.can_receive ?? false) || setPreferences.isPending}
                  aria-label={t('notificationPolicy.personalToggle', {
                    category: tr(`notificationPolicy.categories.${category.id}.title`),
                  })}
                  onChange={(event) => {
                    setPreferences.mutate({ [category.id]: event.target.checked });
                  }}
                />
                <span>{t('notificationPolicy.forMe')}</span>
              </label>
            </li>
          );
        })}
      </ul>
      <p className={styles.note}>
        {t('notificationPolicy.mandatory', {
          list: preferences.data.mandatory
            .map((id) => tr(`notificationPolicy.mandatoryKinds.${id}`))
            .join(', '),
        })}
      </p>
    </div>
  );
}
